import React, { useState, useEffect } from 'react';
import { BarChart3, Workflow, Brain, CheckCircle2, Star, Mail, Phone, MapPin, Calendar, ArrowRight, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { mockData } from '../mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  BarChart3: BarChart3,
  Workflow: Workflow,
  Brain: Brain
};

const Home = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [calendlyUrl, setCalendlyUrl] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });

  // Fetch Calendly URL on component mount
  useEffect(() => {
    const fetchCalendlyUrl = async () => {
      try {
        const response = await axios.get(`${API}/settings/calendly-url`);
        if (response.data.success && response.data.url) {
          setCalendlyUrl(response.data.url);
        }
      } catch (error) {
        console.error('Error fetching Calendly URL:', error);
        // Use default URL from mock data
        setCalendlyUrl(mockData.contact.calendlyUrl);
      }
    };
    
    fetchCalendlyUrl();
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setFormData({ name: '', email: '', company: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      if (error.response?.data?.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else {
        toast.error('Failed to send message. Please try again later.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const openCalendly = () => {
    const urlToOpen = calendlyUrl || mockData.contact.calendlyUrl;
    window.open(urlToOpen, '_blank');
  };

  return (
    <div className="home-container">
      {/* Navigation Header */}
      <header className="nav-header">
        <div className="nav-logo">
          <img 
            src="https://customer-assets.emergentagent.com/job_94eccfea-4dcc-423a-a23c-0454ec56d235/artifacts/kaoxzv9a_ss%20white%20logo.png" 
            alt="SteelSaints Logo" 
            className="logo-image"
          />
        </div>
        
        <nav className="nav-links desktop-nav">
          <button onClick={() => scrollToSection('services')} className="nav-link">Services</button>
          <button onClick={() => scrollToSection('case-studies')} className="nav-link">Case Studies</button>
          <button onClick={() => scrollToSection('about')} className="nav-link">About</button>
          <button onClick={() => scrollToSection('contact')} className="nav-link">Contact</button>
        </nav>

        <div className="nav-cta desktop-nav">
          <Button onClick={openCalendly} className="btn-primary">
            <Calendar className="icon-sm" />
            Schedule Call
          </Button>
        </div>

        <button className="mobile-menu-btn" onClick={() => setIsMenuOpen(!isMenuOpen)}>
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </header>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="mobile-menu">
          <button onClick={() => scrollToSection('services')} className="mobile-menu-item">Services</button>
          <button onClick={() => scrollToSection('case-studies')} className="mobile-menu-item">Case Studies</button>
          <button onClick={() => scrollToSection('about')} className="mobile-menu-item">About</button>
          <button onClick={() => scrollToSection('contact')} className="mobile-menu-item">Contact</button>
          <Button onClick={openCalendly} className="btn-primary" style={{ width: '100%', marginTop: '1rem' }}>
            <Calendar className="icon-sm" />
            Schedule Call
          </Button>
        </div>
      )}

      {/* Hero Section */}
      <section className="hero-section" id="home">
        <div className="hero-content">
          <div className="hero-badge">SteelSaints</div>
          <h1 className="hero-title">{mockData.hero.title}</h1>
          <p className="hero-subtitle">{mockData.hero.subtitle}</p>
          <div className="hero-cta-group">
            <Button onClick={openCalendly} className="btn-primary btn-large">
              {mockData.hero.ctaPrimary}
              <ArrowRight className="icon-sm" />
            </Button>
            <Button onClick={() => scrollToSection('services')} className="btn-secondary btn-large">
              {mockData.hero.ctaSecondary}
            </Button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-container" id="services">
        <div className="section-header">
          <div className="section-label">Our Services</div>
          <h2 className="heading-2">Solutions That Drive Results</h2>
          <p className="body-large text-secondary">Comprehensive solutions engineered for competitive advantage</p>
        </div>
        <div className="services-grid">
          {mockData.services.map((service) => {
            const IconComponent = iconMap[service.icon];
            return (
              <Card key={service.id} className="service-card">
                <CardContent className="service-card-content">
                  <div className="service-icon-wrapper">
                    <IconComponent className="service-icon" />
                  </div>
                  <h3 className="heading-3">{service.title}</h3>
                  <p className="body-medium text-secondary">{service.description}</p>
                  <ul className="feature-list">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="feature-item">
                        <CheckCircle2 className="icon-xs text-brand" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="section-container section-alt" id="case-studies">
        <div className="section-header">
          <div className="section-label">Case Studies</div>
          <h2 className="heading-2">Proven Impact</h2>
          <p className="body-large text-secondary">Real results from real partnerships</p>
        </div>
        <div className="case-studies-container">
          {mockData.caseStudies.map((study) => (
            <Card key={study.id} className="case-study-card">
              <CardContent className="case-study-content">
                <div className="case-study-header">
                  <div>
                    <h3 className="heading-3">{study.client}</h3>
                    <p className="body-small text-secondary">{study.industry} • {study.duration}</p>
                  </div>
                </div>
                
                <div className="case-study-section">
                  <h4 className="case-study-label">Challenge</h4>
                  <p className="body-medium">{study.challenge}</p>
                </div>

                <div className="case-study-section">
                  <h4 className="case-study-label">Solution</h4>
                  <p className="body-medium">{study.solution}</p>
                </div>

                <div className="case-study-section">
                  <h4 className="case-study-label">Results</h4>
                  <div className="results-grid">
                    {study.results.map((result, idx) => (
                      <div key={idx} className="result-item">
                        <CheckCircle2 className="icon-sm text-brand" />
                        <span className="body-medium">{result}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="testimonial-quote">
                  <p className="body-medium italic">"{study.testimonial}"</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-container" id="testimonials">
        <div className="section-header">
          <div className="section-label">Testimonials</div>
          <h2 className="heading-2">What Clients Say</h2>
        </div>
        <div className="testimonials-grid">
          {mockData.testimonials.map((testimonial) => (
            <Card key={testimonial.id} className="testimonial-card">
              <CardContent className="testimonial-content">
                <div className="star-rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="star-icon" fill="currentColor" />
                  ))}
                </div>
                <p className="body-large testimonial-text">"{testimonial.content}"</p>
                <div className="testimonial-author">
                  <div>
                    <p className="body-medium font-semibold">{testimonial.name}</p>
                    <p className="body-small text-secondary">{testimonial.role} at {testimonial.company}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* About Section */}
      <section className="section-container section-alt" id="about">
        <div className="about-content">
          <div className="about-text">
            <div className="section-label" style={{ textAlign: 'center' }}>About SteelSaints</div>
            <h2 className="heading-2">{mockData.about.title}</h2>
            <p className="body-large" style={{ marginTop: '1.5rem' }}>{mockData.about.description}</p>
            <div className="expertise-grid">
              {mockData.about.expertise.map((item, idx) => (
                <div key={idx} className="expertise-item">
                  <CheckCircle2 className="icon-sm text-brand" />
                  <span className="body-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-container" id="contact">
        <div className="section-header">
          <div className="section-label">Contact</div>
          <h2 className="heading-2">Let's Build Something Exceptional</h2>
          <p className="body-large text-secondary">Ready to transform your business? Let's talk.</p>
        </div>
        
        <div className="contact-grid">
          <div className="contact-info">
            <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>Get In Touch</h3>
            
            <div className="contact-item">
              <Mail className="icon-md text-brand" />
              <div>
                <p className="body-small text-secondary">Email</p>
                <p className="body-medium">{mockData.contact.email}</p>
              </div>
            </div>

            <div className="contact-item">
              <MapPin className="icon-md text-brand" />
              <div>
                <p className="body-small text-secondary">Location</p>
                <p className="body-medium">{mockData.contact.location}</p>
              </div>
            </div>

            <Button onClick={openCalendly} className="btn-primary" style={{ width: '100%', marginTop: '2rem' }}>
              <Calendar className="icon-sm" />
              Schedule a Consultation
            </Button>
          </div>

          <Card className="contact-form-card">
            <CardContent className="contact-form-content">
              <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>Send a Message</h3>
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Name *</label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Your name"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email *</label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="your.email@example.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="company" className="form-label">Company</label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="Your company name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">Message *</label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    placeholder="Tell us about your project..."
                  />
                </div>

                <Button type="submit" className="btn-primary" style={{ width: '100%' }} disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  {!isSubmitting && <ArrowRight className="icon-sm" />}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-brand">
            <img 
              src="https://customer-assets.emergentagent.com/job_94eccfea-4dcc-423a-a23c-0454ec56d235/artifacts/kaoxzv9a_ss%20white%20logo.png" 
              alt="SteelSaints Logo" 
              className="footer-logo"
            />
            <p className="body-small" style={{ marginTop: '0.75rem' }}>Engineering intelligence into every decision for NYC businesses.</p>
          </div>
          
          <div className="footer-links">
            <div className="footer-column">
              <h4 className="footer-heading">Services</h4>
              <button onClick={() => scrollToSection('services')} className="footer-link">Data Analytics</button>
              <button onClick={() => scrollToSection('services')} className="footer-link">Automation</button>
              <button onClick={() => scrollToSection('services')} className="footer-link">AI Integration</button>
            </div>
            
            <div className="footer-column">
              <h4 className="footer-heading">Company</h4>
              <button onClick={() => scrollToSection('about')} className="footer-link">About Us</button>
              <button onClick={() => scrollToSection('case-studies')} className="footer-link">Case Studies</button>
              <button onClick={() => scrollToSection('contact')} className="footer-link">Contact</button>
            </div>
          </div>
        </div>
        
        <div className="footer-bottom">
          <p className="body-small text-secondary">© 2025 SteelSaints. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
